import { Fragment, forwardRef, type ForwardedRef, useState, useEffect } from 'react'
import cn from 'classnames'
import { twMerge } from 'tailwind-merge'
import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import { Input, type InputProps } from '../input/input'

export interface SelectOption {
  value: string
  label?: string
}

export interface SelectProps extends Omit<InputProps, 'onChange'> {
  options?: SelectOption[]
  onChange?: (_value: SelectOption['value']) => void
  isPII?: boolean
}

const Select = forwardRef<HTMLInputElement, SelectProps>(function Select(
  { options, label, className, disabled, error, value, onChange, isPII, ...props },
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [selectedOption, setSelectedOption] = useState(options?.find((option) => option.value === value))

  useEffect(() => {
    setSelectedOption(options?.find((option) => option.value === value))
  }, [options, value])

  const optionsClass = twMerge(
    'absolute z-10 w-full mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-zinc-900 ring-opacity-5 focus:outline-none sm:text-sm',
    className,
  )

  return (
    <Listbox
      value={selectedOption ?? { value: '', label: '' }}
      onChange={(v) => {
        setSelectedOption(v)
        onChange && onChange(v.value)
      }}
      disabled={disabled}
    >
      {({ open }) => (
        <div className="relative">
          <Listbox.Button className="w-full text-left focus:outline-none">
            <Input
              {...props}
              ref={ref}
              label={label}
              value={selectedOption?.label ?? selectedOption?.value ?? ''}
              error={error}
              readOnly
              disabled={disabled}
              isPII={isPII}
              className={cn(
                {
                  'cursor-pointer': !disabled,
                },
                'pb-3xs',
                className,
              )}
              icon={
                open && !disabled ? (
                  <ChevronUpIcon
                    className={cn('ml-2 h-5 w-5', {
                      'cursor-pointer text-gray-400': !disabled,
                      'text-gray-400': disabled,
                    })}
                    aria-hidden="true"
                  />
                ) : (
                  <ChevronDownIcon
                    className={cn('ml-2 h-5 w-5', {
                      'cursor-pointer text-gray-400': !disabled,
                      'text-gray-400': disabled,
                    })}
                    aria-hidden="true"
                  />
                )
              }
            />
          </Listbox.Button>
          <Transition
            show={Boolean(open && !disabled)}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className={optionsClass}>
              {options?.map((option) => (
                <Listbox.Option key={option.value} value={option}>
                  {({ active }) => (
                    <div
                      data-testid={`${option.value.replace(' ', '_').toLocaleLowerCase()}_option`}
                      className={cn('relative cursor-default select-none py-2 pl-3 pr-9', {
                        'bg-gray-50 text-zinc-900': active,
                        'text-gray-900': !active,
                        'fs-exclude': isPII,
                      })}
                    >
                      <span className="block truncate">{option.label}</span>
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  )
})

export { Select }
